import Vue from 'vue'
import { ref, computed } from '@vue/composition-api'
import { apiToastSuccess } from '@/@core/utils/toast'
import { CONFIRM_ORDER_PRODUCTS } from '@/store/modules/order.module'
import store from '@/store'
import constants, { FLAG_CONSTANTS } from '@/constants'

const { ORDER_STATUS, ORDER_PRODUCT_SHIPMENT_STATUS } = constants

// Off until the flag store has loaded, so the option never flashes up and
// disappears.
const isPartialConfirmFlagOn = () => {
  const hasFeature = store.getters && store.getters['flags/hasFeature']
  return typeof hasFeature === 'function'
    && Boolean(hasFeature(FLAG_CONSTANTS.KP_4363_PARTIAL_ORDER_CONFIRMATION))
}

// KP-4363: order statuses a brand can confirm selected lines from.
export const PARTIAL_CONFIRM_ORDER_STATUSES = [
  ORDER_STATUS.ACCEPTED,
  ORDER_STATUS.MODIFIED,
  ORDER_STATUS.PARTIALLY_CONFIRMED,
]

// Line statuses that still wait for the brand's confirmation. A legacy line
// with no status counts as pending, as it does on the backend.
const PENDING_LINE_STATUSES = [
  ORDER_PRODUCT_SHIPMENT_STATUS.IN_PROCESS.LABEL,
  ORDER_PRODUCT_SHIPMENT_STATUS.READY_TO_SHIP.LABEL,
  undefined,
  null,
]

export const isPartiallyConfirmedOrder = order => order?.status === ORDER_STATUS.PARTIALLY_CONFIRMED

// Back to Received on a confirmed or partially confirmed order gives stock
// back, so the brand confirms it in a popup first.
export const isRevertConfirmationNeeded = order => order?.status === ORDER_STATUS.CONFIRMED
  || isPartiallyConfirmedOrder(order)

export const isConfirmedLine = product => product?.status === ORDER_PRODUCT_SHIPMENT_STATUS.CONFIRMED.LABEL

// A confirmed line stays locked once it is ticked for shipping or shipped: its
// status then reads selectedByUser / partiallyShipped, so the backend's
// `isConfirmed` flag says it. The shipment being created edits its own copies.
export const isLockedConfirmedLine = (product, order) => isConfirmedLine(product)
  || (!!product?.isConfirmed && !order?.isDataForCreateShipment)

export const isPendingConfirmLine = product => !!product
  && PENDING_LINE_STATUSES.includes(product.status)
  && !product.isDeletedByBrand
  && !product.isDeletedByRetailer

export const getPendingConfirmProductIds = (products = []) => (products || [])
  .filter(isPendingConfirmLine)
  .map(product => product.productId)

// Order statuses on which a brand can remove a line from the order.
const BRAND_REMOVE_ORDER_STATUSES = [
  ORDER_STATUS.ACCEPTED,
  ORDER_STATUS.MODIFIED,
  ORDER_STATUS.PARTIALLY_CONFIRMED,
]

const isClosedLine = (product, order) => product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL
  || isLockedConfirmedLine(product, order)

// The brand's changes wait for the retailer: a pending_retailer order, or a
// partially confirmed order after Save + notify (KP-4363).
const isAwaitingRetailer = order => order.status === ORDER_STATUS.PENDING_RETAILER
  || (isPartiallyConfirmedOrder(order) && !!order.isPendingRetailerConfirmation)

const canRetailerRemove = (product, order, isRetailer) => !!isRetailer
  && !!product.isEdited
  && isAwaitingRetailer(order)

/**
 * Whether the remove button shows on a line: never on a cancelled or a
 * confirmed (KP-4363) line; for the brand on an accepted, modified or
 * partially confirmed order; for the retailer on a line the brand edited
 * while the order waits for the retailer (pending_retailer, or partially
 * confirmed after Save + notify).
 */
export const isOrderLineRemovable = (product, order, { isBrand, isRetailer }) => {
  if (isClosedLine(product, order)) return false
  return (isBrand && BRAND_REMOVE_ORDER_STATUSES.includes(order.status))
    || canRetailerRemove(product, order, isRetailer)
}

const readConfirmResponse = res => ({
  message: res?.data?.message || 'Products confirmed successfully',
  data: res?.data?.data,
})

export const buildConfirmProductsPayload = (productIds = []) => ({
  productIds: [...new Set((productIds || []).filter(Boolean))],
})

export function useOrderPartialConfirm() {
  const isConfirmingProducts = ref(false)
  const selectedProductIds = ref({})

  const isBrand = computed(() => store.getters.isBrand)

  // The KP-4363 feature flag hides it in the UI; the backend can also switch
  // it off for an order by sending isPartialConfirmEnabled: false.
  function isPartialConfirmEnabled(order, isShipment = false) {
    return !!order
      && isPartialConfirmFlagOn()
      && !isShipment
      && !order.shipmentId
      && isBrand.value
      && order.isPartialConfirmEnabled !== false
      && PARTIAL_CONFIRM_ORDER_STATUSES.includes(order.status)
  }

  function isLineSelectableForConfirm(product, order, isShipment = false) {
    return isPartialConfirmEnabled(order, isShipment) && isPendingConfirmLine(product)
  }

  function isLineSelectedForConfirm(product) {
    return !!selectedProductIds.value[product?.productId]
  }

  function toggleLineForConfirm(product, isSelected) {
    if (!isPendingConfirmLine(product)) return
    Vue.set(selectedProductIds.value, product.productId, !!isSelected)
  }

  function getSelectedForConfirmIds(products = []) {
    return getPendingConfirmProductIds(products)
      .filter(productId => selectedProductIds.value[productId])
  }

  function isAllPendingSelected(products = []) {
    const pendingIds = getPendingConfirmProductIds(products)
    return pendingIds.length > 0
      && pendingIds.every(productId => selectedProductIds.value[productId])
  }

  function toggleAllPendingForConfirm(products = [], isSelected) {
    getPendingConfirmProductIds(products).forEach(productId => {
      Vue.set(selectedProductIds.value, productId, !!isSelected)
    })
  }

  function clearConfirmSelection() {
    selectedProductIds.value = {}
  }

  function confirmSelectedProducts(orderId, productIds, loadOrder) {
    const payload = buildConfirmProductsPayload(productIds)
    if (!payload.productIds.length) {
      return Promise.resolve({ success: false })
    }
    isConfirmingProducts.value = true
    return store
      .dispatch(CONFIRM_ORDER_PRODUCTS, { orderId, payload })
      .then(res => {
        isConfirmingProducts.value = false
        const { message, data } = readConfirmResponse(res)
        apiToastSuccess(message)
        if (loadOrder) loadOrder(orderId)
        return { success: true, data }
      })
      .catch(err => {
        // The axios interceptor already shows the backend message.
        isConfirmingProducts.value = false
        return { success: false, err }
      })
  }

  return {
    // State
    isConfirmingProducts,
    selectedProductIds,

    // Methods
    isPartialConfirmEnabled,
    isLineSelectableForConfirm,
    isLineSelectedForConfirm,
    toggleLineForConfirm,
    getSelectedForConfirmIds,
    isAllPendingSelected,
    toggleAllPendingForConfirm,
    clearConfirmSelection,
    confirmSelectedProducts,
    isConfirmedLine,
    isLockedConfirmedLine,
    isPendingConfirmLine,
    isPartiallyConfirmedOrder,
  }
}
